import { Subtenant } from '@bioliberty/lifemind';
import { ClinicSessionUtilization, InternalUtilization, UtilizationMonth } from '../../api/internal';
import { MetricPart, UTILIZATION_METRICS, UtilizationMetric } from '../components/utilizationMetrics';
import type { MonthlySessionsSummary } from './useMonthlySessions';
import type { ClinicRow, MonthColumn, UtilizationData } from './useUtilizationData';

// Builds the dashboard tables from the per-clinic, per-month aggregates returned by patient-services.

const EMPTY: UtilizationData = { clinics: [], months: [], totals: new Map() };

export function buildUtilizationData(
  utilization: InternalUtilization | undefined,
  subtenants: Subtenant[] | undefined,
  tenantIds: string[] | undefined,
  metric: UtilizationMetric
): UtilizationData {
  if (
    !utilization ||
    (!utilization.sessionTotals.length && !utilization.newPatientTotals.length) ||
    !subtenants?.length
  ) {
    return EMPTY;
  }

  const config = UTILIZATION_METRICS[metric];
  const numerator = aggregatePart(utilization, config.numerator);
  const denominator = config.denominator ? aggregatePart(utilization, config.denominator) : null;

  if (!numerator.earliestMonth) {
    return EMPTY;
  }

  const clinicCells = combineClinicCells(numerator.clinicCells, denominator?.clinicCells);
  const rawTotals = combineTotals(numerator.totals, denominator?.totals);

  const months = generateMonthRange(numerator.earliestMonth, new Date());

  const sortedSubtenants = [...subtenants].filter(({ name }) => tenantIds?.includes(name)).sort((a, b) => a.id - b.id);

  const clinics = buildClinicRows(sortedSubtenants, months, clinicCells);
  const filledTotals = buildTotals(months, rawTotals);

  return { clinics, months, totals: filledTotals };
}

export function buildMonthlySessions(
  clinicSessions: ClinicSessionUtilization[],
  subtenants: Subtenant[] | undefined,
  tenantIds: string[] | undefined
): MonthlySessionsSummary[] {
  return clinicSessions
    .map(({ subtenantName, year, month, sessionCount, distinctPatients, totalReps, totalTimeTaken }) => {
      // Cut at the first '_' as the Sessions tab always has, which leaves clinics with '_' in their name out
      const [tenantId] = `${subtenantName}`.split('_');
      const clinic = subtenants?.find(({ name }) => name === tenantId)?.label || tenantId;

      return {
        year,
        month: fromServerMonth(month),
        tenantId,
        clinic,
        patients: distinctPatients,
        sessions: sessionCount,
        totalReps,
        totalTimeTaken,
        repsPerPatient: distinctPatients > 0 ? totalReps / distinctPatients : 0,
        repsPerSession: sessionCount > 0 ? totalReps / sessionCount : 0,
        timeTakenPerPatient: distinctPatients > 0 ? totalTimeTaken / distinctPatients : 0,
        timeTakenPerSession: sessionCount > 0 ? totalTimeTaken / sessionCount : 0,
        sessionsPerPatient: distinctPatients > 0 ? sessionCount / distinctPatients : 0
      };
    })
    .filter((row) => tenantIds?.includes(row.tenantId));
}

interface AggregatedData {
  clinicCells: Map<string, Map<string, number>>;
  totals: Map<string, number>;
  earliestMonth: UtilizationMonth | null;
}

function aggregatePart(utilization: InternalUtilization, part: MetricPart): AggregatedData {
  const { clinicSessions, sessionTotals, clinicNewPatients, newPatientTotals } = utilization;

  if (part === 'newPatients') {
    return aggregateMonths(clinicNewPatients, newPatientTotals, (row) => row.count);
  }
  if (part === 'distinctPatients') {
    return aggregateMonths(clinicSessions, sessionTotals, (row) => row.distinctPatients);
  }
  if (part === 'sessionCount') {
    return aggregateMonths(clinicSessions, sessionTotals, (row) => row.sessionCount);
  }
  if (part.sum === 'duration') {
    return aggregateMonths(clinicSessions, sessionTotals, (row) => row.durationMs / 1000);
  }
  const field = part.sum;
  return aggregateMonths(clinicSessions, sessionTotals, (row) => row[field]);
}

function aggregateMonths<T extends UtilizationMonth>(
  clinicRows: (T & { subtenantName: string })[],
  totalRows: T[],
  getValue: (row: T) => number
): AggregatedData {
  const clinicCells = new Map<string, Map<string, number>>();
  for (const row of clinicRows) {
    let clinicMonths = clinicCells.get(row.subtenantName);
    if (!clinicMonths) {
      clinicMonths = new Map();
      clinicCells.set(row.subtenantName, clinicMonths);
    }
    clinicMonths.set(toMonthKey(row.year, fromServerMonth(row.month)), getValue(row));
  }

  const monthTotals = new Map<string, number>();
  let earliestMonth: UtilizationMonth | null = null;
  for (const row of totalRows) {
    monthTotals.set(toMonthKey(row.year, fromServerMonth(row.month)), getValue(row));

    if (
      !earliestMonth ||
      row.year < earliestMonth.year ||
      (row.year === earliestMonth.year && row.month < earliestMonth.month)
    ) {
      earliestMonth = row;
    }
  }

  return { clinicCells, totals: monthTotals, earliestMonth };
}

function combineClinicCells(
  numerator: Map<string, Map<string, number>>,
  denominator: Map<string, Map<string, number>> | undefined
): Map<string, Map<string, number>> {
  if (!denominator) {
    return numerator;
  }
  const result = new Map<string, Map<string, number>>();
  for (const [tenantId, numMonths] of numerator) {
    const denomMonths = denominator.get(tenantId);
    const cells = new Map<string, number>();
    for (const [monthKey, numValue] of numMonths) {
      const denomValue = denomMonths?.get(monthKey) ?? 0;
      cells.set(monthKey, denomValue > 0 ? numValue / denomValue : 0);
    }
    result.set(tenantId, cells);
  }
  return result;
}

function combineTotals(
  numerator: Map<string, number>,
  denominator: Map<string, number> | undefined
): Map<string, number> {
  if (!denominator) {
    return numerator;
  }
  const result = new Map<string, number>();
  for (const [monthKey, numValue] of numerator) {
    const denomValue = denominator.get(monthKey) ?? 0;
    result.set(monthKey, denomValue > 0 ? numValue / denomValue : 0);
  }
  return result;
}

function buildClinicRows(
  subtenants: Subtenant[],
  months: MonthColumn[],
  clinicCells: Map<string, Map<string, number>>
): ClinicRow[] {
  return subtenants.map((subtenant) => {
    const cellSource = clinicCells.get(subtenant.name);
    const cells = new Map<string, number>();

    let firstActiveMonth: string | null = null;
    for (const month of months) {
      const value = cellSource?.get(month.key) ?? 0;
      cells.set(month.key, value);

      if (value > 0 && !firstActiveMonth) {
        firstActiveMonth = month.key;
      }
    }

    return {
      tenantId: subtenant.name,
      label: subtenant.label,
      firstActiveMonth,
      cells
    };
  });
}

function buildTotals(months: MonthColumn[], rawTotals: Map<string, number>): Map<string, number> {
  const totals = new Map<string, number>();
  for (const month of months) {
    totals.set(month.key, rawTotals.get(month.key) ?? 0);
  }
  return totals;
}

function fromServerMonth(month: number): number {
  return month - 1;
}

function toMonthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function generateMonthRange(earliest: UtilizationMonth, latest: Date): MonthColumn[] {
  const months: MonthColumn[] = [];
  const current = new Date(earliest.year, fromServerMonth(earliest.month), 1);
  const end = new Date(latest.getFullYear(), latest.getMonth(), 1);

  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();
    months.push({
      key: toMonthKey(year, month),
      year,
      month,
      label: current.toLocaleString('en-US', { month: 'short', year: 'numeric' })
    });
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}
